import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Modal, Platform, Pressable, ScrollView, View } from 'react-native';
import { CoreConfig } from '../../../CoreConfig';
import { Configuration } from '../../../Domain/DTOs/Configuration/ConfigurationDTO';
import { component } from '../../Decorators/Component';
import { AnimatedTextView } from '../Typography/AnimatedTextView';
import { AnimatedPressable } from '../Buttons/AnimatedPressable';
import { IconComponent } from '../Icons/IconComponent';
import { SearchItemView } from './SearchItemView';
import { CardView } from '../Cards/CardView';
import { useInteractionsSheet } from '../../Hooks/UseInteractionsSheet';
import { useAnimation } from '../Animations/AnimationHooks';
const CenterPosition = Platform.select({
    default: 16,
    android: 16,
    ios: 16
});
const TopPosition = Platform.select({
    default: -8,
    android: -8,
    ios: -8,
});
export const SelectView = component({
    provider: CoreConfig.provider,
    moduleName: CoreConfig.moduleName,
    componentName: Configuration.ComponentNames.select,
    style(theme, mediaQuery, window, props) {
        var _a;
        const padding = props.size === 'sm'
            ? 5
            : 15;
        const height = props.size === 'sm'
            ? 30
            : 50;
        const fontSize = props.size === 'sm'
            ? 14
            : 16;
        const lineHeight = fontSize + 2;
        return {
            container: {
                maxHeight: height,
                minWidth: props.size === 'sm'
                    ? 100
                    : 200,
                width: '100%'
            },
            input: {
                flexDirection: 'row',
                borderRadius: 8,
                borderWidth: 1,
                backgroundColor: '#fff',
                borderStyle: 'solid',
                marginVertical: 1,
                marginHorizontal: 3
            },
            placeholder: Object.assign({ minHeight: 18, fontSize: 16, lineHeight: 18, color: 'rgb(115, 135, 159)' }, props.disabled && {
                opacity: 0.4
            }),
            text: Object.assign({ minHeight: lineHeight, fontSize,
                lineHeight, color: '#394E66' }, props.disabled && {
                opacity: 0.4
            }),
            modal: {
                flex: 1
            },
            card: {
                overflow: 'hidden',
                position: 'absolute',
                borderColor: ((_a = theme.colors) === null || _a === void 0 ? void 0 : _a.secondary_color) || 'rgb(17, 85, 204)'
            },
            body: {
                paddingTop: 0,
                paddingBottom: 0,
                paddingHorizontal: 0
            },
            label: Object.assign({ zIndex: 1, lineHeight,
                fontSize, color: 'rgba(115, 135, 159, 1)', paddingHorizontal: 2, backgroundColor: '#fff', position: 'absolute' }, props.disabled && {
                color: 'rgba(115, 135, 159, 0.4)',
            }),
            after: {
                padding,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F0F3F7',
                borderStyle: 'solid',
                borderLeftWidth: 1,
                borderTopRightRadius: 8,
                borderBottomRightRadius: 8,
            },
            icon: Object.assign({ color: '#63778F' }, props.disabled && {
                opacity: 0.4
            }),
            inputContainer: {
                flex: 1,
                minHeight: height - 2,
                padding
            },
            actionSheet: {
                display: mediaQuery.sm({})
                    ? 'none'
                    : 'flex'
            },
        };
    }
})(({ style, value, size, disabled, label, placeholder, values, containerStyle, localization, onSelect, getKey, getItem }) => {
    var _a, _b, _c, _d, _e;
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);
    const [colorAnimation, setColorAnimation] = useAnimation({ initialValue: 0, duration: 200, nativeDriver: false });
    const [scale, setScale] = useAnimation({ initialValue: (value || placeholder) && label ? 0.9 : 1, duration: 100, nativeDriver: false });
    const [top, setTop] = useAnimation({ initialValue: (value || placeholder) ? TopPosition : CenterPosition, duration: 100, nativeDriver: false });
    const { showInteractionsSheet } = useInteractionsSheet();
    const [cardTop, setCardTop] = useState(0);
    const [cardLeft, setCardLeft] = useState(0);
    const [cardWidth, setCardWidth] = useState(0);
    const handleSelect = useCallback((value) => {
        setIsOpen(false);
        setColorAnimation(0);
        onSelect === null || onSelect === void 0 ? void 0 : onSelect(value);
    }, [onSelect]);
    const handleHover = useCallback(() => {
        if (disabled) {
            return;
        }
        setColorAnimation(1);
    }, [disabled]);
    const handleBlur = useCallback(() => !isOpen && setColorAnimation(0), [isOpen]);
    const handleOpen = useCallback(() => {
        var _a, _b;
        if (disabled) {
            return;
        }
        setColorAnimation(1);
        setIsOpen(true);
        if (((_a = style === null || style === void 0 ? void 0 : style.actionSheet) === null || _a === void 0 ? void 0 : _a.display) === 'flex') {
            const interactions = (values || []).map((value, i) => {
                const action = getKey(value, i).toString();
                const text = getItem(value);
                return {
                    action,
                    text,
                    type: 'dense',
                    onPress: () => {
                        setIsOpen(false);
                        setColorAnimation(0);
                        onSelect === null || onSelect === void 0 ? void 0 : onSelect(value);
                    }
                };
            });
            const cancel = {
                action: 'cancel',
                text: 'Cancel',
                type: 'dense',
                onPress: () => {
                    setIsOpen(false);
                    setColorAnimation(0);
                }
            };
            if (Platform.OS === 'android') {
                interactions.unshift(cancel);
            }
            else {
                interactions.push(cancel);
            }
            showInteractionsSheet({ interactions, cancelKey: cancel.action });
            return;
        }
        const rect = (_b = ref.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
        setCardLeft(rect.left);
        setCardTop(rect.top + rect.height + 10);
        setCardWidth(rect.width);
    }, [values, onSelect, ref, (_a = style === null || style === void 0 ? void 0 : style.actionSheet) === null || _a === void 0 ? void 0 : _a.display, disabled]);
    const handleScrollView = useCallback((ref) => {
        if (!ref) {
            return;
        }
        const index = (values || []).findIndex(item => value === item);
        ref.scrollTo({ x: 0, y: index * 54, animated: false });
    }, [values, value]);
    const handleClose = useCallback(() => {
        setIsOpen(false);
        setColorAnimation(0);
    }, []);
    useEffect(() => {
        if (value) {
            setTop(TopPosition);
            setScale(0.9);
        }
        else {
            setTop(CenterPosition);
            setScale(1);
        }
    }, [value]);
    const borderColor = colorAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: ['rgb(191, 204, 218)', ((_b = style === null || style === void 0 ? void 0 : style.card) === null || _b === void 0 ? void 0 : _b.borderColor) || 'rgb(17, 85, 204)']
    });
    const labelStyles = [
        style === null || style === void 0 ? void 0 : style.label,
        {
            left: 15,
            color: colorAnimation.interpolate({
                inputRange: [0, 1],
                outputRange: [((_c = style === null || style === void 0 ? void 0 : style.label) === null || _c === void 0 ? void 0 : _c.color) || 'rgb(115, 135, 159)', ((_d = style === null || style === void 0 ? void 0 : style.card) === null || _d === void 0 ? void 0 : _d.borderColor) || 'rgb(17, 85, 204)']
            }),
            transform: [
                { translateY: top },
                { scale }
            ]
        }
    ];
    return (_jsxs(View, { ref: ref, style: [style === null || style === void 0 ? void 0 : style.container, containerStyle], children: [!!label && (_jsx(AnimatedTextView, Object.assign({ textStyle: labelStyles }, {
                onMouseEnter: handleHover,
                onMouseLeave: handleBlur
            }, { children: label }))), _jsxs(AnimatedPressable, Object.assign({ style: [style === null || style === void 0 ? void 0 : style.input, { borderColor }], onPress: handleOpen }, {
                onMouseEnter: handleHover,
                onMouseLeave: handleBlur
            }, { children: [_jsxs(View, { style: style === null || style === void 0 ? void 0 : style.inputContainer, children: [!value && !!placeholder && (_jsx(AnimatedTextView, { textStyle: style === null || style === void 0 ? void 0 : style.placeholder, children: placeholder })), !!getItem(value) && (_jsx(AnimatedTextView, { textStyle: style === null || style === void 0 ? void 0 : style.text, children: getItem(value) }))] }), _jsx(Animated.View, { style: [style === null || style === void 0 ? void 0 : style.after, { borderColor }], children: _jsx(IconComponent, { containerStyle: style === null || style === void 0 ? void 0 : style.icon, color: (_e = style === null || style === void 0 ? void 0 : style.icon) === null || _e === void 0 ? void 0 : _e.color, name: isOpen ? 'si-arrow-up' : 'si-arrow-down', size: 8 }) })] })), _jsx(Modal, { visible: isOpen, transparent: true, animationType: "fade", onRequestClose: handleClose, onDismiss: handleClose, children: _jsx(Pressable, { onPress: handleClose, style: style === null || style === void 0 ? void 0 : style.modal, children: _jsx(CardView, { containerStyles: Object.assign(Object.assign({}, style === null || style === void 0 ? void 0 : style.card), { top: cardTop, left: cardLeft, width: cardWidth }), bodyStyles: style === null || style === void 0 ? void 0 : style.body, shadowLevel: 0, children: _jsxs(ScrollView, { ref: handleScrollView, style: Platform.OS === 'web' ? { flex: 1, maxHeight: 200 } : { maxHeight: 200 }, nestedScrollEnabled: true, children: [values === null || values === void 0 ? void 0 : values.map((item, index) => (_jsx(SearchItemView, { value: item, getItem: getItem, onPress: handleSelect, size: size }, getKey(item, index)))), (values || []).length <= 0 && (_jsx(SearchItemView, { value: (localization === null || localization === void 0 ? void 0 : localization.no_items) || (localization === null || localization === void 0 ? void 0 : localization.no_results_found) || 'No results found!', getItem: value => value }))] }) }) }) })] }));
});
